/**
 * Reuniões de Camada — estrutura padrão TPL
 */

import type { LstCargo, LstLayerAction, LstLayerMeetingKey } from '../types';

export interface TplLayerMeetingSection {
  id: string;
  title: string;
  prompt: string;
}

export const TPL_LAYER_MEETING_SECTIONS: TplLayerMeetingSection[] = [
  { id: 'aderencia', title: 'Aderência ao TPL', prompt: 'Percentual de rotinas executadas vs planejadas.' },
  { id: 'resultados', title: 'Resultados', prompt: 'KPIs, metas e entregas do período.' },
  { id: 'obstaculos', title: 'Obstáculos', prompt: 'Barreiras que impediram a execução plena.' },
  { id: 'escalonamentos', title: 'Escalonamentos', prompt: 'Itens que exigem suporte da camada superior.' },
  { id: 'plano-acao', title: 'Plano de Ação', prompt: 'Ações, responsáveis e prazos acordados.' },
];

export interface TplLayerMeetingDuration {
  minMinutes: number;
  maxMinutes: number;
  label: string;
}

export const TPL_LAYER_DURATIONS: Record<LstLayerMeetingKey, TplLayerMeetingDuration> = {
  'lider-supervisor': { minMinutes: 15, maxMinutes: 30, label: 'Líder ↔ Supervisor' },
  'supervisor-coordenador': { minMinutes: 30, maxMinutes: 30, label: 'Supervisor ↔ Coordenador' },
  'coordenador-gerente': { minMinutes: 30, maxMinutes: 45, label: 'Coordenador ↔ Gerente' },
  'gerente-diretor': { minMinutes: 45, maxMinutes: 60, label: 'Gerente ↔ Diretor' },
};

export function resolveLayerMeetingKey(
  subordinateCargo: LstCargo,
  leaderCargo: LstCargo,
): LstLayerMeetingKey {
  if (subordinateCargo === 'lider' && leaderCargo === 'supervisor') return 'lider-supervisor';
  if (subordinateCargo === 'supervisor' && leaderCargo === 'coordenador') return 'supervisor-coordenador';
  if (subordinateCargo === 'supervisor' && leaderCargo === 'gerente') return 'supervisor-coordenador';
  if (subordinateCargo === 'coordenador' && leaderCargo === 'gerente') return 'coordenador-gerente';
  if (subordinateCargo === 'gerente' && leaderCargo === 'diretor') return 'gerente-diretor';
  if (leaderCargo === 'diretor') return 'gerente-diretor';
  return 'lider-supervisor';
}

export interface TplLayerMeetingRecord {
  id: string;
  date: string;
  subordinateName: string;
  leaderName: string;
  durationMinutes: number;
  sections: Record<string, string>;
  actions: TplLayerAction[];
  evidenceUrls: string[];
}

export type TplLayerAction = LstLayerAction;

export const TPL_LAYER_SECTION_IDS = [
  'aderencia',
  'resultados',
  'obstaculos',
  'escalonamentos',
  'plano-acao',
] as const;

export type TplLayerSectionId = (typeof TPL_LAYER_SECTION_IDS)[number];

export function isTplLayerSectionId(value: string): value is TplLayerSectionId {
  return (TPL_LAYER_SECTION_IDS as readonly string[]).includes(value);
}

export function normalizeLayerSections(
  sections: Record<string, string>,
): Record<TplLayerSectionId, string> {
  return {
    aderencia: sections.aderencia ?? '',
    resultados: sections.resultados ?? '',
    obstaculos: sections.obstaculos ?? '',
    escalonamentos: sections.escalonamentos ?? '',
    'plano-acao': sections['plano-acao'] ?? '',
  };
}

export function normalizeLayerActions(actions: LstLayerAction[]): LstLayerAction[] {
  return actions.map((action) => ({
    id: action.id,
    description: action.description.trim(),
    responsible: action.responsible.trim(),
    dueDate: action.dueDate,
    status: action.status === 'concluida' ? 'concluida' : 'aberta',
  }));
}

export function buildDefaultLayerMeetingNotes(
  adherencePct: number,
  obstacles: string[],
): Record<string, string> {
  return {
    aderencia: `Aderência TPL: ${adherencePct}%`,
    resultados: '',
    obstaculos: obstacles.join('\n') || '—',
    escalonamentos: '',
    'plano-acao': '',
  };
}
